/**
 * Question selection for quizzes and mixed review.
 *
 * A scope resolves to a set of eligible topics: a single topic, a whole unit,
 * or the entire year. Mixed scopes draw ONLY from topics that are both marked
 * taught and have a working generator, so a quiz never tests material the class
 * has not reached or a topic without a generator yet. The selection is
 * round-robin across eligible topics for balance, then filled.
 */

import type { Difficulty, Question, QuestionSignature } from "../topics/types";
import type { TopicId, UnitId } from "../curriculum/curriculum";
import {
  TaughtSet,
  TOPICS,
  topicsInUnit,
  getTopic,
} from "../curriculum/curriculum";
import type { SubskillId } from "../curriculum/subskills";
import { getGenerator, hasGenerator } from "../topics/registry";
import { makeSeed } from "../engine/rng";

export type QuizScope =
  | { kind: "topic"; topic: TopicId }
  | { kind: "unit"; unit: UnitId }
  | { kind: "fullYear" }
  // A targeted review whose topics are resolved by the caller (e.g. from the
  // student's weakest subskills). Carrying the list keeps this module free of
  // any dependency on the progress repository.
  | { kind: "recommended"; topics: readonly TopicId[] };

/** Eligible topics for a scope, filtered to taught + generator-backed. */
export function eligibleTopics(
  scope: QuizScope,
  taught: TaughtSet,
): TopicId[] {
  let candidates: TopicId[];
  switch (scope.kind) {
    case "topic":
      candidates = [scope.topic];
      break;
    case "unit":
      candidates = topicsInUnit(scope.unit).map((t) => t.id);
      break;
    case "fullYear":
      candidates = taught.list().map((t) => t.id);
      break;
    case "recommended":
      candidates = [...scope.topics];
      break;
  }
  return candidates.filter((id) => {
    // A single explicitly chosen topic is allowed even if not in the taught
    // set (the student picked it); mixed scopes require taught.
    const taughtOk = scope.kind === "topic" ? true : taught.has(id);
    return taughtOk && hasGenerator(id);
  });
}

/**
 * Build a list of questions for a quiz. Distributes questions round-robin
 * across the eligible topics so a unit/full-year quiz is balanced. Uses
 * recent-signature dedup within the selection.
 */
export function selectQuizQuestions(
  scope: QuizScope,
  difficulty: Difficulty,
  count: number,
  taught: TaughtSet,
): Question[] {
  const topics = eligibleTopics(scope, taught);
  if (topics.length === 0) {
    throw new Error("no eligible topics for this quiz scope");
  }

  const recent: QuestionSignature[] = [];
  const questions: Question[] = [];
  for (let i = 0; i < count; i++) {
    const topicId = topics[i % topics.length] as TopicId;
    const gen = getGenerator(topicId);
    if (!gen) continue; // eligibility already filtered, defensive
    const q = gen.generate({
      seed: makeSeed(),
      difficulty,
      recent: [...recent],
    });
    recent.push(gen.signature(q));
    if (recent.length > 16) recent.shift();
    questions.push(q);
  }
  return questions;
}

/**
 * Turn a list of weak subskills (from progress diagnostics) into the topics to
 * review: every taught, generator-backed topic that exercises one of those
 * subskills, ordered by teaching sequence. Pure - the caller supplies the
 * subskills so this module stays independent of the progress repository.
 */
export function recommendedTopics(
  weakSubskills: readonly SubskillId[],
  taught: TaughtSet,
): TopicId[] {
  if (weakSubskills.length === 0) return [];
  const weak = new Set<SubskillId>(weakSubskills);
  return TOPICS.filter(
    (t) =>
      hasGenerator(t.id) &&
      taught.has(t.id) &&
      t.subskills.some((s) => weak.has(s)),
  )
    .sort((a, b) => a.order - b.order)
    .map((t) => t.id);
}

/** Human label for a scope, for headings. */
export function scopeLabel(scope: QuizScope): string {
  switch (scope.kind) {
    case "topic":
      return getTopic(scope.topic).name;
    case "unit":
      return "Unit Review";
    case "fullYear":
      // Draws from every taught topic across the full 6th-grade year.
      return "Full Year Review";
    case "recommended":
      return "Recommended Review";
  }
}
